import { Stack, Tab, TabList, TabPanel, TabPanels, Tabs } from "@carbon/react";
import { FC, useEffect, useState } from "react";

import {
  SelectedTabType,
  useTabNavigation,
  UseTabNavigationProps,
} from "src/components/TabbedView/useTabNavigation.ts";
import { DynamicViewBaseProps } from "src/dynamic-view-renderer/DynamicViewRenderer.tsx";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { DashboardDefinition } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types.ts";
import { RowRenderer } from "src/dynamic-view-renderer/RowRenderer.tsx";

export type TabbedViewParams = {
  onNavigate?: (tab: SelectedTabType) => void;
  isStateful?: boolean;
} & Partial<Pick<UseTabNavigationProps, "pathPrefix">>;
export type TabbedViewProps = DynamicViewBaseProps & TabbedViewParams;

const TabbedView: FC<TabbedViewProps> = ({
  isStateful = true,
  viewConfiguration,
  data,
  pathPrefix,
  onNavigate,
}) => {
  const schema = useDynamicViewSchema();
  const currentDashboardDefinitions: DashboardDefinition[] = schema.dashboardDefinitions.filter(
    (definition) => viewConfiguration.dashboards.includes(definition.code)
  );

  const tabs = currentDashboardDefinitions.map((dashboard) => ({
    code: dashboard.code,
    label: dashboard.name,
  }));

  const { navigateTab: navigateStatefulTab, selectedTab: statefulSelectedTab } = useTabNavigation({
    tabs,
    pathPrefix,
    skip: !isStateful,
  });
  const [notStatefulSelectedTab, setNotStatefulSelectedTab] = useState<
    SelectedTabType | undefined
  >();

  const onTabChange = ({ selectedIndex }: { selectedIndex: number }) => {
    if (isStateful) {
      navigateStatefulTab(selectedIndex);
      return;
    }
    const tab = tabs[selectedIndex] ?? tabs[0];
    setNotStatefulSelectedTab({ ...tab, index: selectedIndex });
  };

  const selectedTab = isStateful ? statefulSelectedTab : notStatefulSelectedTab;
  useEffect(() => {
    selectedTab && onNavigate && onNavigate(selectedTab);
  }, [onNavigate, selectedTab]);

  return (
    <Tabs selectedIndex={selectedTab?.index ?? 0} onChange={onTabChange}>
      <TabList aria-label="List of tabs" className="mb-5">
        {tabs.map((tab) => (
          <Tab key={tab.code}>{tab.label}</Tab>
        ))}
      </TabList>
      <TabPanels>
        {currentDashboardDefinitions.map((dashboard) => (
          <TabPanel className="-mx-5 overflow-auto flex-[1_1_0] pt-0" key={dashboard.code}>
            <Stack gap={5}>
              <RowRenderer rows={dashboard.rows} data={data} />
            </Stack>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};

export default TabbedView;
